/**
 * LA PESTANA OPERACIONAL DESDE LO QUE PASO, NO DE LO ANOTADO (PROYECTOS-ALINEADOS-T1; Ares #39414 punto 2,
 * boceto P #39424, SI Ares #39430).
 *
 * Antes: cambios recientes, proximos pasos y bloqueos eran listas escritas a mano en status_metadata, todas del
 * 14-ago y sin fecha por item (panel-v2 decia v2.2.71 con 2.2.832 servido). Ahora:
 *   - cambios recientes: los ultimos [VIVO] y [MERGE] del dueno del repo y de Ares que nombran el proyecto;
 *   - proximos pasos: las tareas abiertas del dueno (GET /api/v1/tasks?assignee=&status=), con su mision y su edad;
 *   - bloqueos: los [BLOCKED] del dueno de los ultimos 7 dias que ningun evento posterior cita en responde_a;
 *   - lo anotado a mano queda abajo, con su fecha si la tiene y con el veredicto de su dueno si lo revisaron
 *     (status_metadata.hitos_revisados: [{hito, veredicto, motivo, por, fecha}]). Nada se borra: Josse decide.
 *
 * REGLAS (las cuida scripts/check-operacional-alineado.mjs):
 *   - un item viejo sin fecha dice "sin fecha": no se le inventa una;
 *   - al guardar, lo que ya existia conserva su forma y su fecha; solo lo nuevo recibe la fecha de hoy;
 *   - un bloqueo que ya tuvo respuesta (responde_a) no se muestra como abierto.
 *
 * Sin DOM.
 * @module application/proyectos/operacionalDelProyecto
 */

const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());
const DIA_MS = 24 * 60 * 60 * 1000;

function notasDe(e) {
  let n = e && e.notas;
  if (typeof n === 'string') { try { n = JSON.parse(n); } catch { n = null; } }
  return n && typeof n === 'object' ? n : {};
}
const fechaDe = (e) => txt(e && (e.tsUtc || e.ts_utc));
const esDelDueno = (e, origenes) => !!(origenes && origenes.has(e.origen));
// Un evento nombra el proyecto por su clave (panel-v2) o por el nombre de su repo (nx-integrator): los [MERGE] de
// Ares dicen el repo ("nx-integrator 652 -> 331f518"), no la clave.
const nombra = (e, nombres) => {
  const n = typeof e.notas === 'string' ? e.notas : JSON.stringify(e.notas || '');
  const t = (txt(e.titulo) + ' ' + n).toLowerCase();
  return nombres.some((k) => t.includes(k));
};

/** El nombre del repo desde repository_url (https://github.com/org/nx-integrator(.git)). '' si no hay. */
export function repoDe(repositoryUrl) {
  const m = /([^/]+?)(?:\.git)?\/?$/.exec(txt(repositoryUrl));
  return m ? m[1].toLowerCase() : '';
}

/** Los ultimos [VIVO] y [MERGE] del proyecto: del dueno, o de cualquiera que nombre el proyecto o su repo. */
export function cambiosRecientes(eventos = [], { projectKey, repo = '', origenesDueno, tope = 5 } = {}) {
  const nombres = [txt(projectKey).toLowerCase(), txt(repo).toLowerCase()].filter(Boolean);
  return eventos
    .filter((e) => /^\[(VIVO|MERGE)\]/.test(txt(e.titulo)) && (esDelDueno(e, origenesDueno) || nombra(e, nombres)))
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, tope)
    .map((e) => ({ id: e.id, titulo: txt(e.titulo), fecha: fechaDe(e), origen: e.origen }));
}

/** Los [BLOCKED] del dueno de los ultimos `dias` que ningun evento posterior cita en notas.responde_a. */
export function bloqueosAbiertos(eventos = [], { origenesDueno, ahora = Date.now(), dias = 7 } = {}) {
  const citados = new Set();
  for (const e of eventos) {
    const r = notasDe(e).responde_a;
    for (const id of Array.isArray(r) ? r : [r]) if (id !== undefined && id !== null) citados.add(String(id));
  }
  return eventos
    .filter((e) => /^\[BLOCKED\]/.test(txt(e.titulo)) && esDelDueno(e, origenesDueno))
    .filter((e) => { const t = Date.parse(fechaDe(e)); return Number.isFinite(t) ? ahora - t <= dias * DIA_MS : true; })
    .filter((e) => !citados.has(String(e.id)))
    .sort((a, b) => Number(b.id) - Number(a.id))
    .map((e) => ({ id: e.id, titulo: txt(e.titulo), fecha: fechaDe(e), necesita: txt(notasDe(e).needed_from) }));
}

/** Las tareas abiertas del dueno: en curso primero, despues las mas viejas; las congeladas aparte. */
export function proximosPasos(tareas = [], ahora = Date.now()) {
  const filas = tareas
    .filter((t) => ['pending', 'in_progress'].includes(txt(t.status).toLowerCase()))
    .map((t) => {
      const meta = t.metadata && typeof t.metadata === 'object' ? t.metadata : {};
      const creada = Date.parse(txt(t.createdAt || t.created_at));
      return {
        id: t.id, titulo: txt(t.title), estado: txt(t.status).toLowerCase(),
        mision: txt(meta.mission_code) || 'sin misión',
        edadDias: Number.isFinite(creada) ? Math.max(0, Math.floor((ahora - creada) / DIA_MS)) : null,
        congelada: !!meta.congelada,
      };
    });
  const orden = (a, b) => (a.estado === b.estado ? (b.edadDias ?? 0) - (a.edadDias ?? 0) : a.estado === 'in_progress' ? -1 : 1);
  return { vivas: filas.filter((f) => !f.congelada).sort(orden), congeladas: filas.filter((f) => f.congelada).sort(orden) };
}

/** Una lista escrita a mano en la forma de la vista: textos viejos (sin fecha) u objetos {texto, fecha}. */
export function itemsAnotados(lista) {
  const arr = Array.isArray(lista) ? lista : [];
  return arr.map((x) => (x && typeof x === 'object'
    ? { texto: txt(x.texto ?? x.text), fecha: txt(x.fecha) || null }
    : { texto: txt(x), fecha: null })).filter((x) => x.texto);
}

/** El veredicto que su dueno le dio a un hito escrito (por texto exacto; en las notas, por frase contenida). */
export function veredictoDe(texto, revisados, { dentro = false } = {}) {
  const t = txt(texto);
  const arr = Array.isArray(revisados) ? revisados : [];
  if (!dentro) return arr.find((r) => txt(r.hito) === t) || null;
  return arr.filter((r) => txt(r.hito) && t.includes(txt(r.hito)));
}

/**
 * Lo que se guarda de una lista editada: lo que ya existia conserva su forma (texto viejo o {texto, fecha}); lo
 * nuevo o cambiado se guarda como {texto, fecha: hoy}. Asi un texto viejo no recibe una fecha que no tenia.
 */
export function listaParaGuardar(valores, originales, hoy) {
  const previos = Array.isArray(originales) ? originales : [];
  const textoDe = (x) => (x && typeof x === 'object' ? txt(x.texto ?? x.text) : txt(x));
  return valores.map(txt).filter(Boolean).map((v) => previos.find((p) => textoDe(p) === v) ?? { texto: v, fecha: hoy });
}
